import React from "react";
import { doc, updateDoc, deleteField } from "../../../config/dataClient";
import { AlertTriangle, AlertCircle } from "lucide-react";
import { db } from "../../../config/firebase";
import { APP_ID } from "../../../config/constants";

// 管理員審核資料變更 Modal
export default function ReviewModal({ targetUser, onClose, confirm, showToast }) {
  const pending = targetUser.pendingProfileUpdate;
  if (!pending) return null;

  const handleApprove = async () => {
    const ok = await confirm({
      title: "核准變更",
      message: "確定核准此次資料變更？",
      confirmText: "核准",
    });
    if (!ok) return;
    // [安全] 僅套用員工可自助編輯的欄位，避免把 role / 薪資 / 權限 / 帳號狀態
    // 等敏感欄位夾帶在 pendingProfileUpdate 裡，被管理員誤核准而寫入。
    const ALLOWED_FIELDS = ["mobile", "personalEmail", "address", "emergencyContact"];
    const updateData = {};
    for (const key of ALLOWED_FIELDS) {
      if (key in pending) updateData[key] = pending[key];
    }
    try {
      await updateDoc(
        doc(db, "artifacts", APP_ID, "users", targetUser.uid, "profile", "main"),
        {
          ...updateData,
          pendingProfileUpdate: deleteField(),
        }
      );
      showToast?.("已核准");
      onClose();
    } catch (e) {
      alert("操作失敗");
      console.error(e);
    }
  };

  const handleReject = async () => {
    const ok = await confirm({
      title: "拒絕變更",
      message: "確定要拒絕此次變更？",
      confirmText: "拒絕",
      danger: true,
    });
    if (!ok) return;
    try {
      await updateDoc(
        doc(db, "artifacts", APP_ID, "users", targetUser.uid, "profile", "main"),
        { pendingProfileUpdate: deleteField() }
      );
      showToast?.("已拒絕");
      onClose();
    } catch (e) {
      alert("操作失敗");
    }
  };

  const formatVal = (val) => {
    if (!val) return "(空)";
    if (typeof val === "object") {
      return Object.entries(val)
        .map(([k, v]) => {
          const map = { name: "姓名", relation: "關係", mobile: "手機" };
          return `${map[k] || k}: ${v}`;
        })
        .join("，");
    }
    return String(val);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 border-t-4 border-orange-500">
        <h3 className="font-bold text-lg text-slate-800 mb-4 border-b pb-2 flex items-center gap-2">
          <AlertTriangle className="text-orange-500" /> 審核資料變更
        </h3>

        <div className="bg-orange-50 p-3 rounded-lg mb-4 text-sm text-orange-800 border border-orange-100 flex items-start gap-2">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <div>
            <span className="font-bold">{targetUser.name}</span>{" "}
            提交了以下個人資料變更申請，請確認：
          </div>
        </div>

        <div className="space-y-3 mb-6 max-h-[60vh] overflow-y-auto text-sm">
          {Object.keys(pending).map((key) => {
            if (key === "updatedAt") return null;

            const oldRaw = targetUser[key] || "";
            const newRaw = pending[key];
            if (JSON.stringify(oldRaw) === JSON.stringify(newRaw)) return null;

            const labels = {
              mobile: "手機號碼",
              personalEmail: "Email",
              address: "通訊地址",
              emergencyContact: "緊急聯絡人",
              bankAccount: "銀行帳號",
              fullName: "中文全名",
            };
            const label = labels[key] || key;

            return (
              <div
                key={key}
                className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm ring-1 ring-orange-100"
              >
                <span className="font-bold text-slate-600 block text-xs uppercase mb-2 bg-slate-100 px-2 py-1 rounded w-fit">
                  {label}
                </span>
                <div className="flex flex-col gap-1 pl-1">
                  <div className="flex items-center gap-2 text-slate-400 text-xs">
                    <span className="w-8 text-right font-mono">原本</span>
                    <span className="line-through truncate opacity-70">
                      {formatVal(oldRaw)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-blue-600 text-sm font-bold bg-blue-50 p-1.5 rounded">
                    <span className="w-8 text-right font-mono text-blue-400 text-xs">
                      變更
                    </span>
                    <span>{formatVal(newRaw)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex gap-2 pt-2 border-t border-slate-100">
          <button
            onClick={handleReject}
            className="flex-1 bg-white border border-red-200 text-red-500 py-2.5 rounded-lg font-bold hover:bg-red-50 transition-colors"
          >
            拒絕
          </button>
          <button
            onClick={handleApprove}
            className="flex-1 bg-orange-500 text-white py-2.5 rounded-lg font-bold hover:bg-orange-600 shadow-md shadow-orange-200 transition-colors"
          >
            核准變更
          </button>
        </div>
        <button
          onClick={onClose}
          className="w-full mt-3 text-slate-400 text-xs hover:text-slate-600"
        >
          暫不處理 (關閉)
        </button>
      </div>
    </div>
  );
}
